import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { TranslateLoader, TranslateModule, TranslationObject } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AccessDeniedComponent } from './access-denied/access-denied.component';
import { HomeModule } from './components/home/home.module';
import { BrandInterceptor } from './core/interceptors/brand.interceptor';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { TokenInterceptor } from './core/interceptors/token.interceptor';
import { AuthStateService } from './shared/services/auth-state.service';
import { BrandService } from './shared/services/brand.service';
import { ToastService } from './core/service/toast.service';
import { LayoutModule } from './layout/layout.module';
import { LoginModule } from './login/login.module';
import { ProductOverviewModule } from './pages/products/overview/product-overview.module';
import { ToastComponent } from './shared/component/toast/toast.component';
import { ProductRepository } from './shared/repositories/products.repository';
import { LoggerService } from './shared/services/logger.service';
import { NotificationService } from './shared/services/notification.service';
import { ProductsService } from './shared/services/products.service';

@NgModule({
  declarations: [AppComponent, ToastComponent, AccessDeniedComponent],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    LayoutModule,
    HttpClientModule,
    HomeModule,
    ProductOverviewModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatCheckboxModule,
    LoginModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],

  providers: [
    ProductRepository,
    NotificationService,
    LoggerService,
    ToastService,
    {
      provide: APP_INITIALIZER,
      useFactory: (brandService: BrandService) => () => brandService.bootstrap(),
      deps: [BrandService],
      multi: true,
    },
    {
      // Clear any stale auth state from a different brand before routes activate.
      // Prevents a valid CompAir session in localStorage from auto-redirecting the
      // user to CompAir when they intentionally open the IR portal (and vice versa).
      provide: APP_INITIALIZER,
      useFactory: (brandService: BrandService, authState: AuthStateService) => () => {
        authState.clearIfProductTypeMismatch(brandService.current.allowedProductTypes);
      },
      deps: [BrandService, AuthStateService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    ProductsService,
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: BrandInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
  return {
    getTranslation(lang: string): Observable<TranslationObject> {
      return http.get<TranslationObject>(`./assets/i18n/${lang}.json`);
    },
  };
}
